import { Area, IGeofence, UserAttributeDefs } from "@iotv/datamodel-core";
import {
  Position,
  PossibleBusinessObject,
  TimedPosition,
} from "@iotv/iotv-v3-types";
import { EffluentPath } from "./EffluentPath";
import { EffluentTracker, allAreaData } from "./EffluentTracker";
import { PivotEffluentSpreader } from "./PivotEffluentSpreader";
import { LateralEffluentSpreader } from "./LateralEffluentSpreader";
import { TravellingEffluentSpreader } from "./TravellingEffluentSpreader";
import { SpreadingGun } from "./SpreadingGun";
import { EffluentSpreaderHelper } from "../utilityFns";

const debug = process.env.REACT_APP_DEBUG && false;

const typeName = "DeadZone";

export type DeadZoneType = IDeadZone &
  Omit<PossibleBusinessObject, "type"> &
  Partial<IGeofence>;

export interface IDeadZone {
  ttl: number;
  path: TimedPosition[]; // traversed path
  spreaderID: string;
  filteredPath?: Position[];
  filteredIrrigatedArea?: Position[];
  mappedPath?: Position[];
  filteredMappedArea?: Position[];
  runDifference?: number;
  irrigatedArea: Position[]; // covered area
  // output: number;
  // duration: number;
  // concentration: number;
}

export class DeadZone extends Area implements IDeadZone {
  constructor(ob: DeadZoneType) {
    super({ ...ob, type: ob.type ?? typeName });
    this.path = ob.path;
    this.spreaderID = ob.spreaderID;
    this.runDifference = ob.runDifference;
    this.irrigatedArea = ob.irrigatedArea;
    // this.output = ob.output;  //comment out
    // this.duration = ob.duration;  //comment out
    // this.concentration = ob.concentration;  //comment out
    this.ttl = ob.ttl;
  }

  ttl: number;
  path: TimedPosition[];
  spreaderID: string;
  filteredPath?: Position[];
  filteredIrrigatedArea?: Position[];
  mappedPath?: Position[];
  filteredMappedArea?: Position[];
  runDifference?: number;

  mappedRun?: string; //pk of run
  irrigatedArea: Position[];

  // output: number;
  // duration: number;
  // concentration: number;
  _areaPointsMapping?: allAreaData;

  getCoverageAndArea(
    spreaderList: EffluentTracker[],
    filter: string = "Mapped",
    effluentRuns?: EffluentPath[],
  ): Position[] {
    let useLogic = this._areaPointsMapping;
    if (useLogic === undefined) {
      let matchedSpreader = spreaderList.find(
        (spreader) => spreader.id === this.spreaderID,
      );

      if (matchedSpreader === undefined) {
        return this.irrigatedArea;
      }

      let details = { ...matchedSpreader, path: this.path };

      let shallowCopy: EffluentTracker;

      switch (matchedSpreader.type) {
        case "PivotEffluentSpreader":
          shallowCopy = new PivotEffluentSpreader(details);
          break;
        case "LateralEffluentSpreader":
          shallowCopy = new LateralEffluentSpreader(details);
          break;
        case "TravellingEffluentSpreader":
          shallowCopy = new TravellingEffluentSpreader(details);
          break;
        case "SpreadingGun":
          shallowCopy = new SpreadingGun(details);
          break;
        default:
          shallowCopy = new EffluentTracker(details);
      }

      if (effluentRuns) {
        // dynamically search for runs
        let filteredRuns = shallowCopy.getRelevantRuns(effluentRuns);
        shallowCopy.path = this.path;
        let expectedPathObj = EffluentSpreaderHelper.getExpectedRunLine(
          shallowCopy,
          filteredRuns,
        );
        if (expectedPathObj) {
          let runRes = shallowCopy.getSpreadOfAllRuns(expectedPathObj.run);
          this._areaPointsMapping = runRes;
          useLogic = runRes;
        }
      }
    }

    switch (filter) {
      case "Raw":
        return useLogic?.raw?.positions || this.irrigatedArea;
      case "Filtered":
        return useLogic?.filtered?.positions || this.irrigatedArea;
      default:
        return useLogic?.mapped?.positions || this.irrigatedArea;
    }
  }

  getUserAttributes(): UserAttributeDefs {
    return {
      ...super.getUserAttributes(),
      name: {
        type: "String",
        editable: true,
        label: "Name",
        name: "name",
        description: "identification",
        validation: {
          regex: /^[\p{L}0-9\s']+$/u,
          pass: false,
          invalidMessage: "Name cannot contain invalid characters",
        },
      },
      output: {
        type: "Number",
        editable: false,
        label: "Output (L)",
        name: "output",
        description: "Flow output of run",
      },
      duration: {
        type: "Number",
        editable: false,
        label: "Duration (min)",
        name: "duration",
        description: "Effluent Spreading Duration",
      },
      description: {
        type: "String",
        editable: true,
        label: "Description",
        description: "description",
      },
      concentration: {
        type: "Number",
        editable: false,
        label: "Concentration (L/m^2)",
        name: "concentration",
        description: "Concentrate of effluent for this run",
      },
    };
  }
}
